import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MaterialDataTable } from '../../table-common/MaterialDataTable';
import { ColumnVisibilityMenu } from '../../table-common/ColumnVisibilityMenu';
import { useAlarmEventLazyRows } from './useAlarmEventLazyRows';
import { DEFAULT_VISIBLE_ENVIRONMENT_COLUMNS } from '../event-history-table/event-history-data';

let formatTimestampFilter;

const formatTimestamp = value => {
  if (!value) return '-';
  if (!formatTimestampFilter) {
    const injector = angular.element('.ng-scope').injector();
    formatTimestampFilter = injector.get('formatTimestampFilter');
  }
  return formatTimestampFilter(value);
};

const asArray = value => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const parseEnvironmentData = data => {
  if (!data) return {};
  if (typeof data === 'object') return data;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const toDisplayValue = value => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return formatTimestamp(value);
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const collectEnvironmentKeys = events => {
  const keys = [];

  events.forEach(event => {
    const env = parseEnvironmentData(event.environmentData);
    Object.keys(env).forEach(key => {
      if (!keys.includes(key)) {
        keys.push(key);
      }
    });
  });

  return keys;
};

const sortChildRowsByName = (rowA, rowB, columnId) => {
  if (rowA.original.rowType !== 'event' || rowB.original.rowType !== 'event') return 0;
  return String(rowA.getValue(columnId) || '').localeCompare(String(rowB.getValue(columnId) || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

const buildEventColumns = envKeys => [
  {
    accessorKey: 'consist',
    header: 'Consist',
    size: 130,
    enableHiding: false,
    Cell: ({ cell, row }) => (
      <>
        {row.original.alarmLabel || row.original.pageStatus || row.original.occurrenceLabel || cell.getValue()}
        {row.original.consistSummary && `: ${row.original.consistSummary}`}
      </>
    ),
  },
  { accessorKey: 'car', header: 'Car', size: 90 },
  { accessorKey: 'timestamp', header: 'Timestamp', size: 220 },
  // Carries the occurrence/consist group label, so it must stay visible for the tree to stay readable.
  {
    accessorKey: 'name',
    header: 'Name',
    size: 190,
    enableHiding: false,
    sortingFn: sortChildRowsByName,
  },
  { accessorKey: 'eventId', header: 'Event ID', size: 90 },
  { accessorKey: 'action', header: 'Action', size: 90 },
  ...envKeys.map(key => ({
    accessorKey: key,
    header: key,
    size: 130,
  })),
];

const getOccurrenceIndex = (occurrence, fallback) => {
  if (occurrence.index !== undefined) return occurrence.index;
  if (occurrence.occurrenceId !== undefined) return occurrence.occurrenceId;
  return fallback;
};

const resolveOccurrencesByConsist = alarm => {
  // Returns: Map<string, Array<Occurrence>>
  const consistMap = new Map();

  if (alarm.occurrences) {
    if (Array.isArray(alarm.occurrences)) {
      alarm.occurrences.forEach(occ => {
        const consistKey = occ.consist || alarm.consist || '-';
        if (!consistMap.has(consistKey)) {
          consistMap.set(consistKey, []);
        }
        consistMap.get(consistKey).push({ ...occ, consist: consistKey });
      });
    } else if (typeof alarm.occurrences === 'object') {
      Object.entries(alarm.occurrences).forEach(([consistKey, occList]) => {
        const effectiveConsist = consistKey && consistKey.trim() !== '' ? consistKey : (alarm.consist || '-');
        if (!consistMap.has(effectiveConsist)) {
          consistMap.set(effectiveConsist, []);
        }
        const list = asArray(occList);
        list.forEach(occ => {
          consistMap.get(effectiveConsist).push({
            ...occ,
            consist: effectiveConsist,
          });
        });
      });
    }
  }

  if (consistMap.size === 0 && alarm.events?.length) {
    const consistKey = alarm.events[0]?.consist || alarm.consist || '-';
    consistMap.set(consistKey, [
      { index: 1, consist: consistKey, eventIds: alarm.events.map(e => e.id) },
    ]);
  }

  return consistMap;
};

const resolveOccurrences = alarm => {
  const map = resolveOccurrencesByConsist(alarm);
  const flattened = [];
  map.forEach(list => {
    flattened.push(...list);
  });
  return flattened;
};

const getOccurrenceEvents = (occurrence, eventById) => {
  if (Array.isArray(occurrence.events) && occurrence.events.length > 0) {
    return occurrence.events.map(ev => {
      const full = (ev?.id != null ? eventById.get(String(ev.id)) : null) || {};
      return { ...full, ...ev };
    });
  }
  if (Array.isArray(occurrence.eventIds) && occurrence.eventIds.length > 0) {
    return occurrence.eventIds
      .map(id => eventById.get(String(id)) || { id, eventId: id })
      .filter(Boolean);
  }
  return [];
};

const toEventRow = (event, parentRowId, eventIndex, envKeys) => {
  const env = parseEnvironmentData(event.environmentData);
  const row = {
    rowId: `${parentRowId}::event::${event.id || event.eventId || eventIndex}`,
    rowType: 'event',
    consist: '',
    car: event.carNumber || event.car || '-',
    timestamp: event.activated ? formatTimestamp(event.activated) : '-',
    eventId: event.eventId !== undefined ? event.eventId : (event.eventHexCode || '-'),
    name: event.name || event.title || '-',
    action: event.action || '-',
  };

  envKeys.forEach(key => {
    row[key] = toDisplayValue(env[key]);
  });

  return row;
};

const toOccurrenceRow = (occurrence, parentRowId, occIndex, eventById, envKeys) => {
  const occurrenceIndex = getOccurrenceIndex(occurrence, occIndex + 1);
  const events = getOccurrenceEvents(occurrence, eventById);
  const rowId = `${parentRowId}::occurrence::${occurrenceIndex}::${occIndex}`;

  const row = {
    rowId,
    rowType: 'occurrence',
    occurrenceLabel: `Occurrence ${occurrenceIndex} (${events.length} event${events.length === 1 ? '' : 's'})`,
    consist: '',
    car: '',
    timestamp: '',
    eventId: '',
    name: '',
    action: '',
  };

  envKeys.forEach(key => {
    row[key] = '';
  });

  row.subRows = events.map((event, eventIndex) =>
    toEventRow(event, rowId, eventIndex, envKeys)
  );

  return row;
};

const toConsistRow = (consistName, occurrences, parentRowId, eventById, envKeys) => {
  const rowId = `${parentRowId}::consist::${consistName}`;
  const totalOccurrences = occurrences.length;
  const totalEvents = occurrences.reduce((sum, occ) => {
    const events = getOccurrenceEvents(occ, eventById);
    return sum + events.length;
  }, 0);

  const occLabel = `${totalOccurrences} occurrence${totalOccurrences === 1 ? '' : 's'}`;
  const eventLabel = `${totalEvents} event${totalEvents === 1 ? '' : 's'}`;

  const row = {
    rowId,
    rowType: 'consist',
    consist: consistName,
    car: '',
    timestamp: '',
    eventId: '',
    consistSummary: `(${occLabel}, ${eventLabel})`,
    name: '',
    action: '',
  };

  envKeys.forEach(key => {
    row[key] = '';
  });

  row.subRows = occurrences.map((occ, occIndex) =>
    toOccurrenceRow(occ, rowId, occIndex, eventById, envKeys)
  );

  return row;
};

const toAlarmRow = (alarm, index, envKeys) => {
  const alarmId = alarm.id || alarm.ruleRunId || index + 1;
  const eventById = new Map(asArray(alarm.events).map(event => [String(event.id), event]));
  const consistMap = resolveOccurrencesByConsist(alarm);

  const rowId = `alarm::${alarmId}`;
  const row = {
    rowId,
    rowType: 'alarm',
    consist: alarm.consist || '-',
    car: '',
    timestamp: alarm.createdTs ? formatTimestamp(alarm.createdTs) : '',
    eventId: '',
    name: alarm.name || `Alarm ${alarmId}`,
    action: '',
  };

  envKeys.forEach(key => {
    row[key] = '';
  });

  const consistRows = [];
  consistMap.forEach((occurrences, consistName) => {
    consistRows.push(toConsistRow(consistName, occurrences, rowId, eventById, envKeys));
  });

  row.subRows = consistRows;
  return row;
};

const toLazyAlarmRow = (alarm, subRows) => ({
  rowId: `alarm::${alarm.id}`,
  rowType: 'alarm',
  alarmId: alarm.id,
  alarmLabel: (
    <span data-testid={`alarm-rule-view-triggered-alarm-name-${alarm.id}`}>
      <strong>Alarm <span data-testid={`alarm-rule-view-triggered-alarm-id-${alarm.id}`}>{alarm.id}</span>: {alarm.name}</strong>
      {' | '}{alarm.occurrenceTotalCount ?? 0} occurrences
      {' | '}{alarm.triggeredCount ?? 0} triggers
      {' | Created: '}{formatTimestamp(alarm.createdTs)}
      {' | Updated: '}{formatTimestamp(alarm.updatedTs)}
      {' | Handled: '}{alarm.isHandled ? 'Yes' : 'No'}
    </span>
  ),
  subRows,
});

const buildExpandedRows = rows => {
  const expanded = {};
  const visit = row => {
    if (row.subRows?.length) {
      expanded[row.rowId] = true;
      row.subRows.forEach(visit);
    }
  };
  rows.forEach(visit);
  return expanded;
};

const getTreeRowClassName = row => {
  if (row.original.rowType === 'occurrence') return 'alarm-event-tree__occurrence-row';
  if (row.original.rowType === 'page') return 'alarm-event-tree__page-row';
  if (row.original.rowType === 'consist' || (row.depth === 0 && row.subRows?.length)) return 'alarm-event-tree__parent-row';
  return 'alarm-event-tree__child-row';
};

const AlarmPageStatus = ({ branch, state, loadPage, containerRef }) => {
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (state?.loading || state?.error || state?.hasMore === false) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) loadPage(branch);
    }, { root: containerRef.current, rootMargin: '0px 0px 300px 0px' });
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [branch, state, loadPage, containerRef]);

  return (
    <span ref={sentinelRef} className="alarm-event-tree__page-status" data-testid={`alarm-event-page-${branch.rowId}`}>
      {state?.loading ? <span role="status">Loading...</span> : (
        <>
          {state?.error && <span role="alert">{state.error}</span>}
          <Tooltip title={state?.error ? 'Retry loading rows' : 'Load more rows'}>
            <IconButton
              size="small"
              aria-label={state?.error ? 'Retry loading rows' : 'Load more rows'}
              data-testid={`alarm-event-load-more-${branch.rowId}`}
              onClick={event => { event.stopPropagation(); loadPage(branch, Boolean(state?.error)); }}
            >
              {state?.error ? <RefreshIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </>
      )}
    </span>
  );
};

const addPagingRow = (row, branches, loadPage, containerRef) => {
  const state = branches[row.rowId];
  if (row.total === 0 || state?.hasMore === false) return row;
  const branch = {
    rowId: row.rowId,
    alarmId: row.alarmId,
    rowType: row.rowType,
    consist: row.consist,
    queryConsist: row.queryConsist,
    occurrenceIndex: row.occurrenceIndex,
    total: row.total,
  };
  row.subRows.push({
    rowId: `${row.rowId}::page`,
    rowType: 'page',
    pageStatus: <AlarmPageStatus branch={branch} state={state} loadPage={loadPage} containerRef={containerRef} />,
  });
  return row;
};

const buildLazyRows = (alarm, branches, envKeys, loadPage, containerRef) => {
  if (!alarm) return [];
  const metadata = alarm.occurrences || {};
  const consists = [...new Set(alarm.uniqueConsistList?.length ? alarm.uniqueConsistList
    : alarm.consistOccurrenceList?.length ? alarm.consistOccurrenceList : Object.keys(metadata))];
  return consists.map(consist => {
    const occurrences = asArray(metadata[consist]);
    const consistCounts = alarm.consistCounts?.[consist] || {};
    const hasOccurrenceMetadata = Object.hasOwn(metadata, consist);
    const occurrenceTotalFromMetadata = hasOccurrenceMetadata ? occurrences.length : undefined;
    const eventTotalFromMetadata = hasOccurrenceMetadata
      ? occurrences.reduce((total, item) => total + asArray(item.eventIds).length, 0)
      : undefined;
    let aggregateEventTotal;
    if (consists.length === 1) aggregateEventTotal = alarm.historyEventTotalCount;
    const occurrenceTotal = consistCounts.occurrenceCount ?? occurrenceTotalFromMetadata;
    const eventTotal = consistCounts.historyEventCount ?? eventTotalFromMetadata ?? aggregateEventTotal;
    const rowId = `alarm::${alarm.id}::consist::${consist}`;
    const totalOccurrences = occurrenceTotal ?? (consists.length === 1 ? alarm.occurrenceTotalCount : undefined);
    const row = {
      rowId,
      alarmId: alarm.id,
      rowType: 'consist',
      consist: consist || alarm.consist || '-',
      queryConsist: consist,
      total: totalOccurrences,
      consistSummary: `(${totalOccurrences ?? '-'} occurrence${totalOccurrences === 1 ? '' : 's'}, ${eventTotal ?? '-'} event${eventTotal === 1 ? '' : 's'})`,
      subRows: [],
    };
    row.subRows = (branches[rowId]?.items || []).map((occurrence, index) => {
      const occurrenceIndex = occurrence.occurrenceIndex;
      const summaryOccurrence = occurrences.find(item => item.index === occurrenceIndex);
      const total = occurrence.eventCount ?? summaryOccurrence?.eventIds?.length;
      const occurrenceRowId = `${rowId}::occurrence::${occurrenceIndex}`;
      const events = branches[occurrenceRowId]?.items || [];
      const occurrenceRow = toOccurrenceRow({ index: occurrenceIndex + 1, events }, rowId, index, new Map(), envKeys);
      occurrenceRow.rowId = occurrenceRowId;
      occurrenceRow.alarmId = alarm.id;
      occurrenceRow.occurrenceIndex = occurrenceIndex;
      occurrenceRow.occurrenceStartEventId = occurrence.occurrenceStartEventId;
      occurrenceRow.total = total;
      occurrenceRow.occurrenceLabel = `Occurrence ${occurrenceIndex + 1} (${total ?? 0} event${total === 1 ? '' : 's'})`;
      occurrenceRow.subRows = events.map((event, eventIndex) => toEventRow(event, occurrenceRowId, eventIndex, envKeys));
      return addPagingRow(occurrenceRow, branches, loadPage, containerRef);
    });
    return addPagingRow(row, branches, loadPage, containerRef);
  });
};

const limitLazyRowsAtPagingBoundary = (rows, expandedRows) => {
  for (const [consistIndex, consist] of rows.entries()) {
    if (!expandedRows[consist.rowId]) continue;
    const occurrenceIndex = consist.subRows.findIndex(occurrence =>
      occurrence.rowType === 'occurrence'
      && expandedRows[occurrence.rowId]
      && occurrence.subRows.some(child => child.rowType === 'page')
    );
    if (occurrenceIndex === -1) continue;
    return [
      ...rows.slice(0, consistIndex),
      { ...consist, subRows: consist.subRows.slice(0, occurrenceIndex + 1) },
    ];
  }
  return rows;
};

export const AlarmEventTreeTable = ({
  alarmEvents = [],
  environmentColumns,
  isLoading = false,
  error = null,
  lazyLoading = false,
  onLoadOccurrences,
  onLoadOccurrenceEvents,
  onLoadError,
  rootLevel = 'consist',
  columns,
  toolbarTitle,
  toolbarTitleTestId = 'alarm-report-results-title',
  isExporting = false,
  isExportDisabled = false,
  exportExcelLabel = 'Excel',
  exportCsvLabel = 'CSV',
  onExportExcel,
  onExportCsv,
}) => {
  const alarms = useMemo(() => asArray(alarmEvents), [alarmEvents]);
  const cacheKey = alarms.length ? JSON.stringify(alarms.map(alarm => alarm.id)) : null;
  const containerRef = useRef(null);
  const { branches, loadPage } = useAlarmEventLazyRows(lazyLoading ? cacheKey : null, onLoadOccurrences, onLoadOccurrenceEvents, onLoadError);

  const allEvents = useMemo(() => {
    const list = [];
    alarms.forEach(alarm => {
      asArray(alarm.events).forEach(e => list.push(e));
      const occurrences = resolveOccurrences(alarm);
      occurrences.forEach(occ => {
        asArray(occ.events).forEach(e => list.push(e));
      });
    });
    if (lazyLoading) {
      Object.entries(branches).forEach(([rowId, branch]) => {
        if (rowId.includes('::occurrence::')) branch.items.forEach(event => list.push(event));
      });
    }
    return list;
  }, [alarms, lazyLoading, branches]);

  const envKeys = useMemo(
    () => [...new Set([...(environmentColumns || []), ...collectEnvironmentKeys(allEvents)])],
    [environmentColumns, allEvents]
  );
  const [expandedRows, setExpandedRows] = useState({});

  const baseRows = useMemo(() => {
    if (lazyLoading) {
      if (rootLevel === 'alarm') {
        return alarms.map(alarm => toLazyAlarmRow(
          alarm, buildLazyRows(alarm, branches, envKeys, loadPage, containerRef)
        ));
      }
      return alarms.flatMap(alarm => buildLazyRows(alarm, branches, envKeys, loadPage, containerRef));
    }
    // Both 'consist' and 'occurrence' rootLevel support Consist as top-level group when alarm root is bypassed
    if (rootLevel === 'consist' || rootLevel === 'occurrence') {
      const consistLevelRows = [];
      alarms.forEach((alarm, alarmIndex) => {
        const alarmId = alarm.id || alarm.ruleRunId || alarmIndex + 1;
        const eventById = new Map(asArray(alarm.events).map(event => [String(event.id), event]));
        const consistMap = resolveOccurrencesByConsist(alarm);

        consistMap.forEach((occurrences, consistName) => {
          consistLevelRows.push(
            toConsistRow(consistName, occurrences, `alarm::${alarmId}`, eventById, envKeys)
          );
        });
      });
      return consistLevelRows;
    }

    return alarms.map((alarm, index) => toAlarmRow(alarm, index, envKeys));
  }, [alarms, rootLevel, envKeys, lazyLoading, branches, loadPage]);

  const rows = useMemo(() => {
    if (!lazyLoading) return baseRows;
    if (rootLevel === 'alarm') {
      return baseRows.map(alarm => ({
        ...alarm, subRows: limitLazyRowsAtPagingBoundary(alarm.subRows, expandedRows),
      }));
    }
    return limitLazyRowsAtPagingBoundary(baseRows, expandedRows);
  }, [baseRows, rootLevel, lazyLoading, expandedRows]);

  const tableColumns = useMemo(() => {
    if (columns?.length) return columns;
    const eventColumns = buildEventColumns(envKeys);
    if (!lazyLoading) return eventColumns;
    return eventColumns.map(column => ({
      ...column,
      sortUndefined: 'last',
      sortingFn: (rowA, rowB, columnId) => {
        if (rowA.original.rowType !== 'event' || rowB.original.rowType !== 'event') return 0;
        return String(rowA.getValue(columnId) ?? '').localeCompare(String(rowB.getValue(columnId) ?? ''), undefined, {
          numeric: true, sensitivity: 'base',
        });
      },
    }));
  }, [columns, envKeys, lazyLoading]);

  const [columnVisibility, setColumnVisibility] = useState({});
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const defaultColumnVisibility = useMemo(() => Object.fromEntries(
    tableColumns.map(column => {
      const columnKey = column.id || column.accessorKey;
      return [columnKey, column.enableHiding === false || !envKeys.includes(columnKey)
        || DEFAULT_VISIBLE_ENVIRONMENT_COLUMNS.has(columnKey)];
    })
  ), [tableColumns, envKeys]);

  useEffect(() => {
    setColumnVisibility(previous => Object.fromEntries(
      Object.entries(defaultColumnVisibility).map(([columnKey, defaultVisible]) =>
        [columnKey, previous[columnKey] === undefined ? defaultVisible : previous[columnKey]])
    ));
  }, [defaultColumnVisibility]);

  useEffect(() => {
    if (!lazyLoading) return;
    setExpandedRows({});
    if (containerRef.current) containerRef.current.scrollTop = 0;
  }, [cacheKey, lazyLoading]);

  useEffect(() => {
    if (!lazyLoading) return;
    const visit = row => {
      if (!expandedRows[row.rowId]) return;
      if (['consist', 'occurrence'].includes(row.rowType) && !branches[row.rowId]) loadPage(row);
      row.subRows?.forEach(visit);
    };
    rows.forEach(visit);
  }, [rows, expandedRows, lazyLoading, branches, loadPage]);

  const hasExportActions = Boolean(onExportExcel) || Boolean(onExportCsv);
  const exportDisabled = isExporting || isExportDisabled;
  const exportMenuOpen = Boolean(exportMenuAnchorEl);
  const handleToggleExportMenu = event => {
    if (!exportDisabled) {
      setExportMenuAnchorEl(event.currentTarget);
    }
  };
  const handleCloseExportMenu = () => setExportMenuAnchorEl(null);
  const handleExportExcel = () => {
    handleCloseExportMenu();
    onExportExcel();
  };
  const handleExportCsv = () => {
    handleCloseExportMenu();
    onExportCsv();
  };
  const toolbarActions = toolbarTitle
    ? (
      <div className="alarm-report-table-toolbar" data-testid="alarm-report-table-toolbar">
        <h4 data-testid={toolbarTitleTestId}>{toolbarTitle}</h4>
      </div>
    )
    : null;
  const toolbarInternalActions = hasExportActions
    ? (
      <span className="alarm-report-export">
        {isExporting && (
          <span className="fetching-span">
            <i className="fas fa-circle-notch fa-spin fa-fw" />Generating export file...
          </span>
        )}
        <Tooltip title="Export">
          <IconButton
            disabled={exportDisabled}
            onClick={handleToggleExportMenu}
            data-testid="alarm-report-export-button"
            aria-label="Export alarm report"
            aria-expanded={exportMenuOpen}
            aria-controls={exportMenuOpen ? 'alarm-report-export-menu' : undefined}
            aria-haspopup="menu"
            size="small"
            className="alarm-report-export__toggle"
          >
            <SaveAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          id="alarm-report-export-menu"
          anchorEl={exportMenuAnchorEl}
          open={exportMenuOpen}
          onClose={handleCloseExportMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={handleExportExcel}
            disabled={exportDisabled}
            data-testid="alarm-report-export-xlsx-button"
          >
            {exportExcelLabel}
          </MenuItem>
          <MenuItem
            onClick={handleExportCsv}
            disabled={exportDisabled}
            data-testid="alarm-report-export-csv-button"
          >
            {exportCsvLabel}
          </MenuItem>
        </Menu>
      </span>
    )
    : null;

  useEffect(() => {
    if (lazyLoading) return;
    setExpandedRows(previous => {
      const next = buildExpandedRows(rows);
      Object.keys(next).forEach(rowId => {
        if (previous[rowId] !== undefined) next[rowId] = previous[rowId];
      });
      return next;
    });
  }, [rows, lazyLoading]);

  if (error) {
    return (
      <p className="form-validator-text-danger" data-testid="alarm-event-tree-error">
        {error}
      </p>
    );
  }

  if (isLoading) {
    return (
      <p data-testid="alarm-event-tree-loading">
        <i className="fas fa-spinner fa-pulse fa-fw" /> Loading events...
      </p>
    );
  }

  if (!isLoading && rows.length === 0) {
    return <p data-testid="alarm-event-tree-empty">No alarm event results found.</p>;
  }

  return (
    <div data-testid="alarm-event-tree-table">
      <MaterialDataTable
        className={`event-history-container consist-event-history alarm-event-tree${lazyLoading ? ' alarm-event-tree--lazy' : ''}`}
        scrollContainerClassName="consist-event-history__scroll"
        ariaLabel="Alarm event occurrence tree"
        columns={tableColumns}
        data={rows}
        isLoading={isLoading}
        enableExpanding
        getRowCanExpand={row => lazyLoading
          ? (row.original.rowType === 'alarm' ? !!row.original.subRows?.length
            : ['consist', 'occurrence'].includes(row.original.rowType) && row.original.total !== 0)
          : !!row.original.subRows?.length}
        getSubRows={row => row.subRows || []}
        getRowId={row => row.rowId}
        expandedRows={expandedRows}
        onExpandedChange={setExpandedRows}
        tableContainerRef={containerRef}
        enableRowVirtualization={lazyLoading}
        rowVirtualizerOptions={lazyLoading ? { estimateSize: () => 40, overscan: 10 } : undefined}
        enableSorting={false}
        enableColumnResizing
        enableTopToolbar
        muiTopToolbarProps={{ sx: { '& > .MuiBox-root': { padding: '0.1rem' } } }}
        enableHiding
        showBuiltInColumnVisibility={false}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        topToolbarActions={toolbarActions}
        toolbarInternalActions={({ table }) => (
          <>
            <ColumnVisibilityMenu
              table={table}
              testIdPrefix="alarm-event-tree"
              buttonTestId="alarm-event-tree-show-hide-columns-button"
              onReset={() => setColumnVisibility(defaultColumnVisibility)}
            />
            {toolbarInternalActions}
          </>
        )}
        muiTableBodyRowProps={({ row }) => ({
          'data-testid': `alarm-report-row-${row.original.rowType}-${row.original.rowId}`,
          className: getTreeRowClassName(row),
          onClick: row.getCanExpand() ? () => row.toggleExpanded() : undefined,
          style: row.getCanExpand() ? { cursor: 'pointer' } : undefined,
        })}
        muiTableBodyCellProps={({ column, row, table }) => {
          const cellTestId = `alarm-report-cell-${row.original.rowType}-${row.original.rowId}-${column.id}`;
          const isParentRow = row.original.rowType === 'consist' || (row.depth === 0 && row.original.subRows?.length);
          const isOccurrenceRow = row.original.rowType === 'occurrence';
          const isPageRow = row.original.rowType === 'page';

          if (!isParentRow && !isOccurrenceRow && !isPageRow) {
            return { 'data-testid': cellTestId };
          }

          if (column.id === 'consist') {
            const visibleColumns = table.getVisibleLeafColumns();
            const consistColumnIndex = visibleColumns.findIndex(({ id }) => id === column.id);
            return {
              'data-testid': cellTestId,
              className: isOccurrenceRow
                ? 'alarm-event-tree__occurrence-label'
                : 'alarm-event-tree__parent-label',
              colSpan: visibleColumns.length - consistColumnIndex,
              style: lazyLoading ? { flex: '1 1 auto', width: 'auto' } : undefined,
            };
          }

          if (column.id !== 'mrt-row-expand') {
            return {
              'data-testid': cellTestId,
              className: isOccurrenceRow
                ? 'alarm-event-tree__occurrence-cell-hidden'
                : 'alarm-event-tree__parent-cell-hidden',
            };
          }

          return {
            'data-testid': cellTestId,
            className: isOccurrenceRow
              ? 'alarm-event-tree__occurrence-expand-cell'
              : 'alarm-event-tree__parent-expand-cell',
            onClick: row.getCanExpand() ? event => {
              event.stopPropagation();
              row.toggleExpanded();
            } : undefined,
          };
        }}
        muiExpandButtonProps={({ row }) => ({
          'data-testid': row.original.rowType === 'alarm' && lazyLoading
            ? `alarm-rule-view-triggered-alarm-toggle-${row.original.alarmId}`
            : `alarm-report-expand-button-${row.original.rowId}`,
        })}
      />
    </div>
  );
};

AlarmEventTreeTable.propTypes = {
  alarmEvents: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  environmentColumns: PropTypes.arrayOf(PropTypes.string),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  lazyLoading: PropTypes.bool,
  onLoadOccurrences: PropTypes.func,
  onLoadOccurrenceEvents: PropTypes.func,
  onLoadError: PropTypes.func,
  rootLevel: PropTypes.oneOf(['consist', 'occurrence', 'alarm']),
  columns: PropTypes.array,
  toolbarTitle: PropTypes.string,
  toolbarTitleTestId: PropTypes.string,
  isExporting: PropTypes.bool,
  isExportDisabled: PropTypes.bool,
  exportExcelLabel: PropTypes.string,
  exportCsvLabel: PropTypes.string,
  onExportExcel: PropTypes.func,
  onExportCsv: PropTypes.func,
};
